import { useEffect, useState, useRef } from 'react';
import { format } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Pencil, Trash2, Loader2, Upload, ImagePlus, RotateCcw, X, FileText, FileUp, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { api, API_BASE } from '@/lib/api';
import { TEST_IDS } from '@/constants/testIds/floreal';
import { errorMessage } from '@/lib/errorMessage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_EVENT = {
    title: '',
    event_type: 'workshop',
    date: '',
    start_time: '10:00',
    end_time: '12:00',
    description: '',
    total_seats: 8,
    available_seats: 8,
    price: '',
    payment_deadline_days: 7,
    payment_instructions: '',
    payment_doc_url: '',
    payment_doc_name: '',
    location: '',
    image_url: '',
    image_scale: 1,
    image_offset_x: 0,
    image_offset_y: 0,
};

function safeDate(iso) {
    try { return format(new Date(iso + 'T00:00:00'), 'd MMM yyyy', { locale: itLocale }); } catch { return iso; }
}

import { resolveImageUrl as resolveImg } from '@/lib/resolveImageUrl';

export default function EventsPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [docUploading, setDocUploading] = useState(false);
    const fileInputRef = useRef(null);
    const docInputRef = useRef(null);
    const [defaultCoords, setDefaultCoords] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/events');
            setItems(res.data || []);
        } catch { toast.error('Impossibile caricare gli eventi'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);
    // Load global payment coordinates to pre-fill new events.
    useEffect(() => {
        api.get('/site').then((r) => setDefaultCoords(r.data?.settings?.ws_payment_instructions || '')).catch(() => {});
    }, []);

    const openNew = () => setEditing({ ...EMPTY_EVENT, _new: true, payment_instructions: defaultCoords });
    const openEdit = (ev) => setEditing({
        ...ev,
        event_type: ev.event_type || 'workshop',
        price: ev.price ?? '',
        payment_deadline_days: ev.payment_deadline_days ?? 7,
        payment_instructions: ev.payment_instructions ?? '',
        payment_doc_url: ev.payment_doc_url ?? '',
        payment_doc_name: ev.payment_doc_name ?? '',
        location: ev.location ?? '',
        image_url: ev.image_url ?? '',
        image_scale: ev.image_scale ?? 1,
        image_offset_x: ev.image_offset_x ?? 0,
        image_offset_y: ev.image_offset_y ?? 0,
    });

    const uploadDoc = async (file) => {
        if (!file) return;
        const okTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        if (!okTypes.includes(file.type)) { toast.error('Formato non supportato. Usa PDF, JPG o PNG.'); return; }
        setDocUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await fetch(`${API_BASE}/admin/events/payment-doc/upload`, {
                method: 'POST', credentials: 'include', body: fd,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.detail || 'Errore upload');
            setEditing((prev) => ({ ...prev, payment_doc_url: data.url, payment_doc_name: data.name }));
            toast.success('Documento caricato');
        } catch (err) {
            toast.error(errorMessage(err, 'Errore upload documento'));
        } finally {
            setDocUploading(false);
            if (docInputRef.current) docInputRef.current.value = '';
        }
    };

    const removeDoc = () => setEditing((prev) => ({ ...prev, payment_doc_url: '', payment_doc_name: '' }));

    const uploadImage = async (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error('Seleziona un file immagine (JPG, PNG, WEBP, GIF)'); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await fetch(`${API_BASE}/admin/gallery/upload`, {
                method: 'POST',
                credentials: 'include',
                body: fd,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.detail || 'Errore upload');
            setEditing((prev) => ({
                ...prev,
                image_url: data.image_url,
                image_scale: 1,
                image_offset_x: 0,
                image_offset_y: 0,
            }));
            toast.success('Immagine caricata');
        } catch (err) {
            toast.error(errorMessage(err, 'Errore upload'));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const resetTransform = () => setEditing((prev) => ({
        ...prev, image_scale: 1, image_offset_x: 0, image_offset_y: 0,
    }));

    const removeImage = () => setEditing((prev) => ({
        ...prev, image_url: '', image_scale: 1, image_offset_x: 0, image_offset_y: 0,
    }));

    const save = async () => {
        if (!editing.title || !editing.date || !editing.start_time || !editing.end_time || !editing.description) {
            toast.error('Compila tutti i campi obbligatori'); return;
        }
        setSaving(true);
        try {
            const payload = {
                title: editing.title,
                event_type: editing.event_type || 'workshop',
                date: editing.date,
                start_time: editing.start_time,
                end_time: editing.end_time,
                description: editing.description,
                price: editing.price === '' ? null : parseFloat(editing.price),
                payment_deadline_days: editing.payment_deadline_days === '' || editing.payment_deadline_days == null ? 7 : parseInt(editing.payment_deadline_days, 10),
                payment_instructions: editing.payment_instructions?.trim() || null,
                payment_doc_url: editing.payment_doc_url || null,
                payment_doc_name: editing.payment_doc_name || null,
                location: editing.location?.trim() || null,
                image_url: editing.image_url || null,
                image_scale: Number(editing.image_scale) || 1,
                image_offset_x: Math.round(Number(editing.image_offset_x) || 0),
                image_offset_y: Math.round(Number(editing.image_offset_y) || 0),
            };
            // Deposit guard removed: bookings now use the full price (no deposit).
            if (editing._new) {
                payload.total_seats = parseInt(editing.total_seats, 10);
                await api.post('/admin/events', payload);
            } else {
                payload.total_seats = parseInt(editing.total_seats, 10);
                // available_seats is system-managed (auto = total - booked); never sent.
                await api.put(`/admin/events/${editing.id}`, payload);
            }
            toast.success(editing._new ? 'Evento creato' : 'Evento aggiornato');
            setEditing(null);
            load();
        } catch (e) {
            toast.error(errorMessage(e, 'Errore salvataggio'));
        } finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm('Eliminare questo evento e le relative iscrizioni?')) return;
        try {
            await api.delete(`/admin/events/${id}`);
            toast.success('Eliminato');
            load();
        } catch { toast.error('Errore eliminazione'); }
    };

    const scale = editing?.image_scale ?? 1;
    const offX = editing?.image_offset_x ?? 0;
    const offY = editing?.image_offset_y ?? 0;

    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <h2 className="font-serif text-xl">Eventi e workshop</h2>
                <Button size="sm" onClick={openNew}><Plus className="h-4 w-4 mr-2" />Aggiungi evento</Button>
            </div>
            <div className="overflow-x-auto">
                <Table data-testid={TEST_IDS.adminEventsTable}>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Data</TableHead>
                            <TableHead>Titolo</TableHead>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Orario</TableHead>
                            <TableHead>Posti</TableHead>
                            <TableHead>Prezzo / Pagamento</TableHead>
                            <TableHead>Stato</TableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Caricamento…</TableCell></TableRow>
                        ) : items.length === 0 ? (
                            <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">Nessun evento. Aggiungine uno per cominciare.</TableCell></TableRow>
                        ) : items.map((e) => (
                            <TableRow key={e.id}>
                                <TableCell>{safeDate(e.date)}</TableCell>
                                <TableCell><div className="font-medium">{e.title}</div></TableCell>
                                <TableCell><Badge variant="outline" className="text-xs capitalize">{e.event_type === 'aperiworkshop' ? 'Aperiworkshop' : 'Workshop'}</Badge></TableCell>
                                <TableCell>{e.start_time}–{e.end_time}</TableCell>
                                <TableCell>{e.available_seats} / {e.total_seats}</TableCell>
                                <TableCell>
                                    <div>{e.price != null ? `${e.price.toFixed(2)} €` : '—'}</div>
                                    <div className="text-xs text-muted-foreground">entro {e.payment_deadline_days != null ? e.payment_deadline_days : 7} giorni</div>
                                </TableCell>
                                <TableCell><Badge variant="outline" className="text-xs">{e.status}</Badge></TableCell>
                                <TableCell className="text-right">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => openEdit(e)}><Pencil className="h-4 w-4 mr-2" />Modifica</DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => remove(e.id)}><Trash2 className="h-4 w-4 mr-2" />Elimina</DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
                    {editing && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl">{editing._new ? 'Nuovo evento' : 'Modifica evento'}</DialogTitle>
                                <DialogDescription>Compila i dettagli e salva.</DialogDescription>
                            </DialogHeader>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                                <div className="sm:col-span-2"><Label className="eyebrow">Titolo *</Label><Input value={editing.title} onChange={(ev) => setEditing({ ...editing, title: ev.target.value })} className="mt-1.5" /></div>
                                <div>
                                    <Label className="eyebrow">Tipologia evento *</Label>
                                    <Select
                                        value={editing.event_type || 'workshop'}
                                        onValueChange={(v) => setEditing((prev) => ({ ...prev, event_type: v }))}
                                    >
                                        <SelectTrigger data-testid={TEST_IDS.adminEventType} className="mt-1.5"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="workshop">Workshop</SelectItem>
                                            <SelectItem value="aperiworkshop">Aperiworkshop</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div><Label className="eyebrow">Data *</Label><Input type="date" value={editing.date} onChange={(ev) => setEditing({ ...editing, date: ev.target.value })} className="mt-1.5" /></div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label className="eyebrow">Inizio *</Label><Input type="time" value={editing.start_time} onChange={(ev) => setEditing({ ...editing, start_time: ev.target.value })} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Fine *</Label><Input type="time" value={editing.end_time} onChange={(ev) => setEditing({ ...editing, end_time: ev.target.value })} className="mt-1.5" /></div>
                                </div>
                                <div><Label className="eyebrow">Posti totali *</Label><Input type="number" min={1} value={editing.total_seats} onChange={(ev) => setEditing({ ...editing, total_seats: ev.target.value })} className="mt-1.5" /></div>
                                {!editing._new && (
                                    <div>
                                        <Label className="eyebrow">Posti disponibili</Label>
                                        <div className="mt-1.5 h-10 flex items-center rounded-md border border-border bg-muted/50 px-3 text-sm text-muted-foreground" data-testid="admin-event-available-readonly">
                                            {editing.available_seats} / {editing.total_seats} <span className="ml-2 text-xs">(calcolato automaticamente)</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">Si aggiorna da solo con le prenotazioni; si libera solo annullando una prenotazione.</p>
                                    </div>
                                )}
                                <div><Label className="eyebrow">Prezzo per persona (€)</Label><Input type="number" step="0.01" min={0} value={editing.price} onChange={(ev) => setEditing({ ...editing, price: ev.target.value })} className="mt-1.5" placeholder="Es. 65,00 — vuoto se gratuito" /></div>
                                <div><Label className="eyebrow">Giorni per il pagamento</Label><Input data-testid={TEST_IDS.adminEventDeposit} type="number" step="1" min={0} value={editing.payment_deadline_days} onChange={(ev) => setEditing({ ...editing, payment_deadline_days: ev.target.value })} className="mt-1.5" placeholder="Es. 7" /><p className="text-xs text-muted-foreground mt-1">Giorni, dalla prenotazione, entro cui versare l'importo intero.</p></div>

                                <div className="sm:col-span-2">
                                    <Label className="eyebrow">Luogo</Label>
                                    <Input
                                        value={editing.location}
                                        onChange={(ev) => setEditing({ ...editing, location: ev.target.value })}
                                        className="mt-1.5"
                                        placeholder='Es. "Atelier Floreal — Via Unione 3, Lomazzo (CO)"'
                                    />
                                </div>

                                {/* Coordinate di pagamento */}
                                <div className="sm:col-span-2 rounded-xl border border-border p-4 bg-atelier-cream/40 space-y-3">
                                    <Label className="eyebrow">Coordinate di pagamento</Label>
                                    <Textarea
                                        data-testid={TEST_IDS.adminEventPaymentInstructions}
                                        rows={3}
                                        value={editing.payment_instructions}
                                        onChange={(ev) => setEditing({ ...editing, payment_instructions: ev.target.value })}
                                        placeholder="Es. Bonifico su IBAN IT00 X000 0000 0000 0000 0000 000 intestato a Floreal Atelier. Causale: nome e cognome + nome evento."
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Puoi inserire le coordinate come testo e/o caricare un documento (PDF, JPG, PNG). Il documento verrà allegato automaticamente all'email di conferma del cliente.
                                    </p>
                                    <input
                                        ref={docInputRef}
                                        type="file"
                                        accept="application/pdf,image/jpeg,image/png"
                                        className="hidden"
                                        onChange={(ev) => uploadDoc(ev.target.files?.[0])}
                                    />
                                    {editing.payment_doc_url ? (
                                        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2.5">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <FileText className="h-4 w-4 text-atelier-bronze shrink-0" />
                                                <span className="text-sm truncate">{editing.payment_doc_name || 'Documento coordinate'}</span>
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                <Button type="button" variant="ghost" size="sm" className="h-8" asChild>
                                                    <a href={resolveImg(editing.payment_doc_url)} target="_blank" rel="noreferrer"><Eye className="h-4 w-4 mr-1" />Anteprima</a>
                                                </Button>
                                                <Button type="button" variant="ghost" size="sm" className="h-8" onClick={() => docInputRef.current?.click()} disabled={docUploading}>
                                                    {docUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Sostituisci
                                                </Button>
                                                <Button type="button" variant="ghost" size="sm" className="h-8 text-destructive hover:text-destructive" onClick={removeDoc} data-testid={TEST_IDS.adminEventDocRemove}>
                                                    <Trash2 className="h-4 w-4 mr-1" />Elimina
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <Button type="button" variant="outline" size="sm" onClick={() => docInputRef.current?.click()} disabled={docUploading} data-testid={TEST_IDS.adminEventDocUpload}>
                                            {docUploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileUp className="h-4 w-4 mr-2" />}
                                            Carica documento (PDF, JPG, PNG)
                                        </Button>
                                    )}
                                </div>

                                {/* Immagine copertina */}
                                <div className="sm:col-span-2 rounded-xl border border-border p-4 bg-atelier-cream/40">
                                    <div className="flex items-center justify-between mb-3">
                                        <Label className="eyebrow">Immagine copertina</Label>
                                        {editing.image_url && (
                                            <button
                                                type="button"
                                                onClick={removeImage}
                                                className="inline-flex items-center gap-1 text-xs text-destructive hover:underline"
                                            >
                                                <X className="h-3 w-3" /> Rimuovi immagine
                                            </button>
                                        )}
                                    </div>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(ev) => uploadImage(ev.target.files?.[0])}
                                    />

                                    {editing.image_url ? (
                                        <>
                                            {/* Preview con transform */}
                                            <div className="rounded-lg overflow-hidden bg-atelier-ivory border border-border relative"
                                                style={{ aspectRatio: '16 / 10' }}>
                                                <img
                                                    src={resolveImg(editing.image_url)}
                                                    alt="Anteprima"
                                                    className="w-full h-full object-cover transition-transform"
                                                    style={{
                                                        transform: `scale(${scale}) translate(${offX}%, ${offY}%)`,
                                                        transformOrigin: 'center center',
                                                    }}
                                                />
                                                <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-widest bg-background/85 backdrop-blur px-2 py-1 rounded-md">
                                                    Anteprima card
                                                </span>
                                            </div>

                                            {/* Controlli */}
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                                                <div>
                                                    <div className="flex justify-between mb-1.5">
                                                        <Label className="text-xs">Zoom</Label>
                                                        <span className="text-xs text-muted-foreground tabular-nums">{scale.toFixed(2)}×</span>
                                                    </div>
                                                    <Slider
                                                        value={[scale * 100]}
                                                        min={100}
                                                        max={300}
                                                        step={5}
                                                        onValueChange={([v]) => setEditing({ ...editing, image_scale: v / 100 })}
                                                    />
                                                </div>
                                                <div>
                                                    <div className="flex justify-between mb-1.5">
                                                        <Label className="text-xs">Sposta orizzontale</Label>
                                                        <span className="text-xs text-muted-foreground tabular-nums">{offX}%</span>
                                                    </div>
                                                    <Slider
                                                        value={[offX]}
                                                        min={-50}
                                                        max={50}
                                                        step={1}
                                                        onValueChange={([v]) => setEditing({ ...editing, image_offset_x: v })}
                                                    />
                                                </div>
                                                <div>
                                                    <div className="flex justify-between mb-1.5">
                                                        <Label className="text-xs">Sposta verticale</Label>
                                                        <span className="text-xs text-muted-foreground tabular-nums">{offY}%</span>
                                                    </div>
                                                    <Slider
                                                        value={[offY]}
                                                        min={-50}
                                                        max={50}
                                                        step={1}
                                                        onValueChange={([v]) => setEditing({ ...editing, image_offset_y: v })}
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex gap-2 mt-4">
                                                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                                                    {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                                                    Cambia immagine
                                                </Button>
                                                <Button type="button" variant="ghost" size="sm" onClick={resetTransform}>
                                                    <RotateCcw className="h-4 w-4 mr-2" /> Reset posizione
                                                </Button>
                                            </div>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading}
                                            className="w-full flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-border bg-background hover:bg-atelier-ivory transition-colors py-10 px-6 text-center"
                                        >
                                            {uploading ? (
                                                <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
                                            ) : (
                                                <ImagePlus className="h-8 w-8 text-muted-foreground" />
                                            )}
                                            <div>
                                                <p className="text-sm font-medium">{uploading ? 'Caricamento…' : 'Carica immagine'}</p>
                                                <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WEBP o GIF · max 10 MB</p>
                                            </div>
                                        </button>
                                    )}
                                </div>

                                <div className="sm:col-span-2"><Label className="eyebrow">Descrizione *</Label><Textarea rows={4} value={editing.description} onChange={(ev) => setEditing({ ...editing, description: ev.target.value })} className="mt-1.5" /></div>
                            </div>
                            <DialogFooter className="mt-4 gap-2">
                                <Button variant="ghost" onClick={() => setEditing(null)}>Annulla</Button>
                                <Button onClick={save} disabled={saving || uploading}>{saving ? 'Salvataggio…' : 'Salva'}</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
